import {DEFAULT_POPUP_FEATURES, OAuthPopup} from "@cbar/oauth-popup";
import {startSilentLogin} from '@cbar/cbar-oauth';
import {PaperSpinnerElement} from "@polymer/paper-spinner/paper-spinner";
import 'whatwg-fetch';

const SILENT_LOGIN_ATTEMPT_KEY = 'cc_silent_login_attempted_at';
const SILENT_LOGIN_BOUNCE_MS = 10000;
const SILENT_LOGIN_TIMEOUT_MS = 8000;
const LOGIN_PATH = '/auth/login';

const getCenterOfElement = element => {
    const rect = element.getBoundingClientRect();

    return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
    };
};

const isInIframe = (): boolean => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

const loginElement = document.querySelector('#login-url');
let authUrl: string | null = null;

if (!!loginElement && !!loginElement.getAttribute('value')) {
    authUrl = loginElement.getAttribute('value');
}

const buildSilentAuthUrl = (url: string): string => {
    try {
        const parsed = new URL(url, window.location.origin);
        if (!parsed.searchParams.has('prompt')) {
            parsed.searchParams.set('prompt', 'none');
        }
        return parsed.toString();
    } catch (e) {
        if (url.indexOf('prompt=') !== -1) {
            return url;
        }
        return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'prompt=none';
    }
};

/**
 * When this page is loaded inside the silent-login iframe, tell the parent
 * whether auth succeeded or failed. Prefer #redirect_url (e.g. /auth/sociallogin)
 * over location.href so we do not re-hit oauth2callback with a spent code.
 */
export const notifySilentLoginParent = () => {
    if (!isInIframe()) {
        return;
    }

    const isLoginPage = window.location.pathname.indexOf('/auth/login') === 0;
    const isGatePage = window.location.pathname.indexOf('/auth/gate') === 0;
    const authFailed = !!document.querySelector('#auth_failed');
    const oauthError = document.querySelector('#oauth_error') as HTMLInputElement | null;
    const redirectInput = document.querySelector('#redirect_url') as HTMLInputElement | null;

    if (isLoginPage || isGatePage || authFailed || (oauthError && oauthError.value)) {
        window.parent.postMessage({
            source: 'cbar-login',
            message: 'error',
            error: (oauthError && oauthError.value) ? oauthError.value : 'User is not logged in',
        }, '*');
        return;
    }

    if (redirectInput && redirectInput.value) {
        window.parent.postMessage({
            source: 'cbar-login',
            message: 'success',
            redirectUrl: redirectInput.value,
        }, '*');
    }
};

export const setupOAuthPopup = () => {
    // Finish oauth2callback handoff before any login-page wiring.
    // (Opener can be null after IdP redirects due to COOP — must fall back.)
    if (document.querySelector('#redirect_url') || document.querySelector('#auth_failed')) {
        return finishOAuthCallbackPage();
    }

    if (OAuthPopup.isPopupWindow()) {
        // We're inside the OAuth popup on a non-callback page (e.g. login).
        return handlePopupCallback();
    }

    // Silent-login iframe should never start another silent login or wire buttons.
    if (isInIframe()) {
        return;
    }

    // Protected-URL gate: silent SSO only, then login page on failure.
    if (document.querySelector('#auth-gate')) {
        return runSilentLoginGate();
    }

    const popup = new OAuthPopup;
    const loginButton: HTMLButtonElement = document.querySelector('#login-button') as HTMLButtonElement;
    const registerButton: HTMLButtonElement = document.querySelector('#register-button') as HTMLButtonElement;
    const spinner: PaperSpinnerElement = document.querySelector('paper-spinner') as PaperSpinnerElement;
    const loadingSection : HTMLDivElement = document.querySelector('#loading-indicator') as HTMLDivElement;
    const header = document.querySelector('cbar-header');

    const handler = function (this: HTMLButtonElement, event, register = false) {
        const e = event as MouseEvent;
        if (typeof e.preventDefault === "function") {
            e.preventDefault();
        }

        if (!authUrl) {
            console.error('OAuth auth URL is missing; cannot open login popup.');
            return;
        }

        let loginLink = authUrl;
        if (register) {
            loginLink += "&register=1";
        }

        const buttonCenter = getCenterOfElement(this);

        // Open the popup around the clicked button
        let features = {
            ...DEFAULT_POPUP_FEATURES,
            top: Math.max(buttonCenter.y - DEFAULT_POPUP_FEATURES.height / 2),
            left: Math.max(buttonCenter.x - DEFAULT_POPUP_FEATURES.width / 2, 0),
        };

        if (popup.isPopupOpen()) {
            // Popup is already open, calling open now will just
            // focus the existing popup.
            return popup.open(authUrl, features);
        }

        if (loginButton) {
            loginButton.hidden = true;
        }
        if(!!registerButton) {
            registerButton.hidden = true;
        }
        if (spinner) {
            spinner.active = true;
        }

        // Update the button text
        const btnText = loadingSection ? loadingSection.innerHTML : '';
        if (loadingSection) {
            loadingSection.innerHTML = 'Enter your login details in the popup...';
        }

        popup.open(loginLink, features)
            .then(({redirectUrl}) => {
                if (loadingSection) {
                    loadingSection.innerHTML = 'Signing you in...';
                }

                window.location.href = redirectUrl;
            }).catch(e => {
            console.error(e);
            if (loginButton) {
                loginButton.hidden = false;
            }
            if(!!registerButton) {
                registerButton.hidden = false;
            }
            if (spinner) {
                spinner.active = false;
            }
            if (loadingSection) {
                loadingSection.innerHTML = btnText;
            }
        });

        window.addEventListener('beforeunload', () => {
            // Close the popup if the window unloads.
            popup.close();
        });
    };

    // Logout page has a login button but no register button — still wire the click.
    if (!!loginButton) {
        loginButton.addEventListener('click', handler.bind(loginButton));
    }

    if (!!header && !!loginButton) {
        header.addEventListener('signup-clicked', handler.bind(loginButton, true));
        header.addEventListener('signin-clicked', handler.bind(loginButton));
    }

    // Direct /auth/login visits: try silent SSO first, then show buttons.
    // If we just came from /auth/gate, bounce protection skips a second attempt.
    if (!!registerButton) {
        registerButton.addEventListener('click', handler.bind(registerButton, true));
        setTimeout(() => {
            checkLoggedInStatus(loginButton, registerButton, spinner, loadingSection);
        }, 500);
    }
};

export const handlePopupCallback = () => {
    return finishOAuthCallbackPage();
};

/**
 * Complete /auth/oauth2callback for popup, iframe, or top-level window.
 * Always falls back to navigating this window when the opener is unavailable.
 */
const finishOAuthCallbackPage = () => {
    const path = window.location.pathname;
    const isLoginPage = path.indexOf('/auth/login') === 0;
    const isGatePage = path.indexOf('/auth/gate') === 0;
    const isLogoutPage = path.indexOf('/auth/logout') === 0;
    const authFailed = !!document.querySelector('#auth_failed');
    const redirectInput = document.querySelector('#redirect_url') as HTMLInputElement | null;
    const redirectUrl = redirectInput && redirectInput.value ? redirectInput.value : null;

    // Login/gate/logout in a popup means auth did not complete — close only.
    if (isLoginPage || isGatePage || isLogoutPage) {
        try {
            window.close();
        } catch (e) {}
        return;
    }

    if (isInIframe()) {
        // notifySilentLoginParent() posts success/error to the parent.
        return;
    }

    if (authFailed || !redirectUrl) {
        if (OAuthPopup.isPopupWindow()) {
            try {
                window.close();
            } catch (e) {}
        }
        return;
    }

    if (OAuthPopup.isPopupWindow()) {
        const notified = OAuthPopup.notifyOpener({redirectUrl});
        if (notified) {
            return;
        }
    }

    // Opener gone or full-page OAuth — finish login here.
    window.location.replace(redirectUrl);
};

const recentlyAttemptedSilentLogin = (): boolean => {
    try {
        const lastAttempt = Number(sessionStorage.getItem(SILENT_LOGIN_ATTEMPT_KEY) || 0);
        return !!lastAttempt && (Date.now() - lastAttempt) < SILENT_LOGIN_BOUNCE_MS;
    } catch (e) {
        return false;
    }
};

const markSilentLoginAttempt = () => {
    try {
        sessionStorage.setItem(SILENT_LOGIN_ATTEMPT_KEY, String(Date.now()));
    } catch (e) {
        // sessionStorage may be unavailable
    }
};

const goToLoginPage = () => {
    window.location.href = LOGIN_PATH;
};

const runSilentLoginGate = () => {
    const status = document.querySelector('#gate-status') as HTMLElement | null;

    if (!authUrl || recentlyAttemptedSilentLogin()) {
        goToLoginPage();
        return;
    }

    markSilentLoginAttempt();

    startSilentLogin({
        authUrl: buildSilentAuthUrl(authUrl),
        timeout: SILENT_LOGIN_TIMEOUT_MS,
    })
        .then((redirectUrl: string) => {
            if (status) {
                status.textContent = 'Signing you in…';
            }
            window.location.href = redirectUrl;
        })
        .catch(e => {
            console.log("Silent login gate failed", e);
            goToLoginPage();
        });
};

const checkLoggedInStatus = (loginButton, registerButton, loadingSpinner, loadingDiv) => {
    const showLoginButton = () => {
        loginButton.hidden = false;

        if (registerButton) {
            registerButton.hidden = false;
        }

        loadingDiv.innerHTML = "";
        loadingSpinner.active = false;
    };

    if (!authUrl) {
        showLoginButton();
        return;
    }

    // Bounce protection: if we just tried silent login (e.g. via /auth/gate)
    // and landed on login, do not immediately retry.
    if (recentlyAttemptedSilentLogin()) {
        showLoginButton();
        return;
    }

    loadingDiv.innerHTML = "Checking if you're already signed in...";
    loadingSpinner.active = true;
    loginButton.hidden = true;
    if (registerButton) {
        registerButton.hidden = true;
    }

    markSilentLoginAttempt();

    startSilentLogin({
        authUrl: buildSilentAuthUrl(authUrl),
        timeout: SILENT_LOGIN_TIMEOUT_MS,
    })
        .then((redirectUrl: string) => {
            loadingDiv.innerHTML = 'Signing you in...';
            window.location.href = redirectUrl;
        })
        .catch(e => {
            console.log("Silent login failed", e);
            showLoginButton();
        });
};
